import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'

// Shared participant-picker modal (explicit exception to the forms-are-pages
// rule). Lists the account's ACTIVE participants; picking one enrolls it in
// `course` — pay_course goes straight to Stripe Checkout, other types land on
// My enrollments. Used by the course list AND the course detail page.
//
// mode = 'enroll'   → POST /courses/{slug}/enroll   (course has free seats)
// mode = 'waitlist' → POST /courses/{slug}/waitlist (course is FULL and has
//                     waitlist_enabled = 1). No payment happens here — the
//                     admin decides that when promoting.
//
// Either way the list comes from /courses/{slug}/participants, which carries
// each participant's status ON THIS COURSE, so someone already registered (or
// already queued) is shown badged and can't be picked twice.

export function ApplyModal({ course, mode = 'enroll', onClose }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [participants, setParticipants] = useState(null) // null = loading
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  const waiting = mode === 'waitlist'

  useEffect(() => {
    api.courses.participants(course.slug)
      .then((d) => setParticipants((d.participants || []).filter((p) => !p.deactivated_at)))
      .catch((e) => { setError(e.code || 'server'); setParticipants([]) })
  }, [course.slug])

  async function apply(p) {
    setBusyId(p.id); setError(null)
    try {
      if (waiting) {
        await api.courses.waitlist(course.slug, { participant_id: p.id })
        navigate('/me/enrollments')
        return
      }
      const d = await api.courses.enroll(course.slug, { participant_id: p.id })
      if (d.checkout_url) {
        window.location.assign(d.checkout_url)
        return
      }
      navigate('/me/enrollments')
    } catch (e) {
      setError(e.code || 'server')
      setBusyId(null)
    }
  }

  // Already on this course (registered, awaiting payment, or queued) — the
  // row is shown for context but can't be picked again.
  const takenLabel = (status) => {
    if (status === 'waitlisted')      return t('courses.status_waitlisted')
    if (status === 'pending_payment') return t('courses.status_pending')
    if (status === 'active')          return t('courses.status_enrolled')
    return null
  }

  const actionLabel = waiting
    ? t('courses.waitlist_confirm')
    : (course.type === 'pay_course' ? t('courses.apply_and_pay') : t('courses.apply_confirm'))

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <section
        className="card form-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '26rem', width: '92%', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <h2>
          {waiting
            ? t('courses.waitlist_title', { course: course.name })
            : t('courses.apply_title',    { course: course.name })}
        </h2>
        <p className="muted">
          {waiting ? t('courses.waitlist_hint') : t('courses.pick_participant')}
        </p>

        {error && (
          <p className="error">
            {t(`courses.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
          </p>
        )}

        {participants === null && <p>{t('common.loading')}</p>}

        {participants !== null && participants.length === 0 && (
          <p>
            {t('courses.no_participants')}{' '}
            <Link to="/settings/participants/new">{t('courses.manage_participants')}</Link>
          </p>
        )}

        {participants !== null && participants.length > 0 && (
          <ul className="participants-list">
            {participants.map((p) => {
              const taken = takenLabel(p.status)
              return (
                <li key={p.id} className="participant-row">
                  <div className="participant-main">
                    <strong>{p.name}</strong>
                    {Number(p.is_self) === 1
                      ? <span className="badge">{t('participants.self_badge')}</span>
                      : null}
                    {taken ? <span className="badge">{taken}</span> : null}
                  </div>
                  <div className="participant-actions">
                    {taken
                      ? <span className="muted">{t('courses.already_on_course')}</span>
                      : (
                        <button type="button" disabled={busyId !== null} onClick={() => apply(p)}>
                          {busyId === p.id ? t('common.loading') : actionLabel}
                        </button>
                      )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        <div className="form-actions">
          <button type="button" className="link-button" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </section>
    </div>
  )
}
